import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiAlertCircle } from 'react-icons/fi';

const BookingForm = ({ date, timeSlot, type, onSubmit, isLoading, user }) => {
  const [formData, setFormData] = useState({
    corriere: '',
    material: '',
    cliente: '',
    destination: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Fetch materiali e autocompila campi
  useEffect(() => {
    fetchMaterials();
    
    setFormData(prev => {
      const updated = { ...prev };
      
      // Se è un corriere, autocompila il campo "Corriere" con il nome azienda
      if (user?.companyType === 'corriere' && user?.companyName) {
        updated.corriere = user.companyName;
      }
      
      // Se è una cartiera, autocompila il campo "Cliente" con il nome azienda
      if (user?.companyType === 'cartiera' && user?.companyName) {
        updated.cliente = user.companyName;
      }
      
      return updated;
    });
  }, [user]);

  const fetchMaterials = async () => {
    try {
      setLoadingMaterials(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/appointments/materials`
      );
      setMaterials(response.data.materials || []);
    } catch (err) {
      console.error('Errore nel caricamento materiali:', err);
      setMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Pulisci errore quando l'utente inizia a modificare
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.corriere.trim()) {
      newErrors.corriere = 'Inserisci il nome del corriere';
    }
    if (!formData.material.trim()) {
      newErrors.material = 'Seleziona il materiale';
    }
    if (!formData.cliente.trim()) {
      newErrors.cliente = 'Inserisci il cliente';
    }
    if (!formData.destination.trim()) {
      newErrors.destination = 'Inserisci la destinazione';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!date || !timeSlot || !type) {
      setErrors({ form: 'Seleziona data, orario e tipo di prenotazione' });
      return;
    }

    onSubmit({
      material: formData.material,
      origin: formData.corriere, // "origin" nel backend rappresenta il corriere
      destination: formData.destination,
      notes: formData.notes,
      date,
      timeSlot,
      type
    });
  };

  if (!date || !timeSlot || !type) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500">Completa i campi sopra per visualizzare il form</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">Dettagli Prenotazione</h3>

      {/* Info riepilogo */}
      <div className="bg-green-50 border border-green-200 rounded p-4 mb-6">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Data</p>
            <p className="font-bold text-gray-800">
              {new Date(date + 'T00:00:00').toLocaleDateString('it-IT', {
                weekday: 'short',
                day: 'numeric',
                month: 'long'
              })}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Orario</p>
            <p className="font-bold text-gray-800">{timeSlot}</p>
          </div>
          <div>
            <p className="text-gray-600">Tipo</p>
            <p className="font-bold text-gray-800 capitalize">{type === 'carico' ? 'Carico' : 'Scarico'}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Nome Corriere */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Corriere *
          </label>
          <input
            type="text"
            name="corriere"
            value={formData.corriere}
            onChange={handleChange}
            placeholder="Es. Corriere Express"
            readOnly={user?.companyType === 'corriere'}
            className={`
              w-full px-4 py-2 border rounded-lg transition
              focus:outline-none focus:ring-2
              ${user?.companyType === 'corriere' ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${errors.corriere
                ? 'border-red-500 focus:ring-red-200 bg-red-50'
                : 'border-gray-300 focus:ring-green-200 focus:border-green-500'
              }
            `}
          />
          {errors.corriere && (
            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
              <FiAlertCircle size={14} /> {errors.corriere}
            </p>
          )}
        </div>

        {/* Materiale Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Materiale *
          </label>
          <select
            name="material"
            value={formData.material}
            onChange={handleChange}
            disabled={loadingMaterials}
            className={`
              w-full px-4 py-2 border rounded-lg transition
              focus:outline-none focus:ring-2
              ${loadingMaterials ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${errors.material
                ? 'border-red-500 focus:ring-red-200 bg-red-50'
                : 'border-gray-300 focus:ring-green-200 focus:border-green-500'
              }
            `}
          >
            <option value="">
              {loadingMaterials ? 'Caricamento...' : 'Scegli il materiale...'}
            </option>
            {materials.map((mat) => (
              <option key={mat.id} value={mat.name}>
                {mat.name}
              </option>
            ))}
          </select>
          {errors.material && (
            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
              <FiAlertCircle size={14} /> {errors.material}
            </p>
          )}
        </div>

        {/* Cliente */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cliente *
          </label>
          <input
            type="text"
            name="cliente"
            value={formData.cliente}
            onChange={handleChange}
            placeholder="Es. Cartiera ACME"
            readOnly={user?.companyType === 'cartiera'}
            className={`
              w-full px-4 py-2 border rounded-lg transition
              focus:outline-none focus:ring-2
              ${user?.companyType === 'cartiera' ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${errors.cliente
                ? 'border-red-500 focus:ring-red-200 bg-red-50'
                : 'border-gray-300 focus:ring-green-200 focus:border-green-500'
              }
            `}
          />
          {errors.cliente && (
            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
              <FiAlertCircle size={14} /> {errors.cliente}
            </p>
          )}
        </div>

        {/* Destinazione */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Destinazione *
          </label>
          <input
            type="text"
            name="destination"
            value={formData.destination}
            onChange={handleChange}
            placeholder="Es. Via Roma 123, Milano"
            className={`
              w-full px-4 py-2 border rounded-lg transition
              focus:outline-none focus:ring-2
              ${errors.destination
                ? 'border-red-500 focus:ring-red-200 bg-red-50'
                : 'border-gray-300 focus:ring-green-200 focus:border-green-500'
              }
            `}
          />
          {errors.destination && (
            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
              <FiAlertCircle size={14} /> {errors.destination}
            </p>
          )}
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Es. Numero ordine, istruzioni speciali..."
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 transition"
          />
        </div>

        {/* Errore generale */}
        {errors.form && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <FiAlertCircle /> {errors.form}
          </div>
        )}

        {/* Bottone submit */}
        <button
          type="submit"
          disabled={isLoading}
          className={`
            w-full py-3 px-4 rounded-lg font-bold text-white transition
            ${isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-500 hover:bg-green-600 active:bg-green-700'
            }
          `}
        >
          {isLoading ? 'Elaborazione...' : 'Conferma Prenotazione'}
        </button>
      </form>
    </div>
  );
};

export default BookingForm;
